/**
 * ------------------------------------------------------------
 * DO NOT MODIFY THIS FILE MANUALLY
 * This file is automatically generated. Any manual changes
 * will be overwritten the next time the file is generated.
 * ------------------------------------------------------------
 * Generated on: 2026-06-24 06:53:07 UTC
 */
import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core';
import { z } from 'zod';

const GeoCoordinate = z.object({
    latitude: z.number(),
    longitude: z.number(),
    openLocationCode: z.union([z.string(), z.null()]),
});
const Address = z.object({
    country: z.string(),
    state: z.string(),
    city: z.string(),
    zip: z.string(),
    street: z.union([z.string(), z.null()]),
    number: z.union([z.string(), z.null()]),
});
const VehicleMaxWeightRestriction = z.object({
    type: z.literal('vehicleMaxWeight'),
    value: z.object({ maxWeight: z.number().describe('Maximum weight in tons') }),
});
const WeekDayName = z.enum(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']);
const OpeningTime = z.object({
    dayOfWeek: z.number().describe('1 = Monday, ..., 7 = Sunday'),
    dayOfWeekName: WeekDayName.describe('Days of the week'),
    start: z.string().describe('Opening time in 24h format'),
    end: z.string().describe('Closing time in 24h format'),
});
const OpeningTimesRestriction = z.object({ type: z.literal('openingTimes'), value: z.array(OpeningTime) });
const Location = z.object({
    id: z.string().uuid(),
    name: z.union([z.string(), z.null()]),
    geoCoordinates: GeoCoordinate.describe('Geographic coordinates of a location'),
    organizationId: z.union([z.number(), z.null()]),
    legacyFactoryId: z.union([z.number(), z.null()]),
    createdAt: z.union([z.string(), z.null()]),
    createdBy: z.union([z.number(), z.null()]),
    address: Address.and(z.union([z.object({}).partial(), z.null()])),
    note: z.union([z.string(), z.null()]),
    updatedAt: z.union([z.string(), z.null()]),
    updatedBy: z.union([z.number(), z.null()]),
    restriction: z.union([
        z.array(z.discriminatedUnion('type', [VehicleMaxWeightRestriction, OpeningTimesRestriction])),
        z.null(),
    ]),
});
const LocationsResponse = z.object({ locations: z.array(Location) });
const Purpose = z.enum(['none', 'create-order', 'create-position']);

export const schemas = {
    GeoCoordinate,
    Address,
    VehicleMaxWeightRestriction,
    WeekDayName,
    OpeningTime,
    OpeningTimesRestriction,
    Location,
    LocationsResponse,
    Purpose,
};

const endpoints = makeApi([
    {
        method: 'get',
        path: '/v1/locations/construction-project/:constructionProjectId',
        alias: 'getLocationsByConstructionProject',
        description: `Returns locations associated with a construction project. Requires authentication and authorization to access the construction project.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'constructionProjectId',
                type: 'Path',
                schema: z.string().uuid().describe('UUID of the construction project'),
            },
            {
                name: 'purpose',
                type: 'Query',
                schema: z
                    .enum(['none', 'create-order', 'create-position'])
                    .describe(
                        `Purpose affects market filtering behavior:
- none: no market filtering is applied
- create-order, create-position: locations filtered by allowed markets`
                    )
                    .optional(),
            },
            {
                name: 'x-operating-market',
                type: 'Header',
                schema: z
                    .enum(['DE', 'AT', 'CZ', 'PL'])
                    .describe('Operating market for filtering locations')
                    .optional()
                    .default('DE'),
            },
        ],
        response: LocationsResponse,
        errors: [
            {
                status: 400,
                description: `Invalid request parameters`,
                schema: z
                    .object({
                        error: z.string(),
                        message: z.string(),
                        validationErrors: z.array(z.object({ field: z.string(), message: z.string() }).partial()),
                    })
                    .partial(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication`,
                schema: z.object({ error: z.string(), message: z.string() }).partial(),
            },
            {
                status: 403,
                description: `Insufficient permissions`,
                schema: z.object({ error: z.string(), message: z.string() }).partial(),
            },
            {
                status: 404,
                description: `Resource not found`,
                schema: z.object({ error: z.string(), message: z.string() }).partial(),
            },
            {
                status: 500,
                description: `Server error`,
                schema: z.object({ error: z.string(), message: z.string() }).partial(),
            },
        ],
    },
    {
        method: 'get',
        path: '/v1/locations/organization/:organizationId',
        alias: 'getLocationsByOrganization',
        description: `Returns locations associated with an organization. Merges locations from monolith and construction project service. Requires authentication and authorization to access the organization.`,
        requestFormat: 'json',
        parameters: [
            {
                name: 'organizationId',
                type: 'Path',
                schema: z.number().int().describe('Integer ID of the organization'),
            },
            {
                name: 'purpose',
                type: 'Query',
                schema: z
                    .enum(['none', 'create-order', 'create-position'])
                    .describe(
                        `Purpose affects market filtering behavior:
- none: no market filtering is applied
- create-order, create-position: locations filtered by allowed markets`
                    )
                    .optional(),
            },
            {
                name: 'x-operating-market',
                type: 'Header',
                schema: z
                    .enum(['DE', 'AT', 'CZ', 'PL'])
                    .describe('Operating market for filtering locations')
                    .optional()
                    .default('DE'),
            },
        ],
        response: LocationsResponse,
        errors: [
            {
                status: 400,
                description: `Invalid request parameters`,
                schema: z
                    .object({
                        error: z.string(),
                        message: z.string(),
                        validationErrors: z.array(z.object({ field: z.string(), message: z.string() }).partial()),
                    })
                    .partial(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication`,
                schema: z.object({ error: z.string(), message: z.string() }).partial(),
            },
            {
                status: 403,
                description: `Insufficient permissions`,
                schema: z.object({ error: z.string(), message: z.string() }).partial(),
            },
            {
                status: 404,
                description: `Resource not found`,
                schema: z.object({ error: z.string(), message: z.string() }).partial(),
            },
            {
                status: 500,
                description: `Server error`,
                schema: z.object({ error: z.string(), message: z.string() }).partial(),
            },
        ],
    },
]);

export const api = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
    return new Zodios(baseUrl, endpoints, options);
}
